import { useState, useEffect, useMemo } from "react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import useProducts from "../../api/products";
import useItems from "../../api/items";
import { smartCompressImage } from "../../lib/imageCompression";
import {
  X,
  Plus,
  Trash2,
  Image as ImageIcon,
  RefreshCw,
  Settings2,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

const ProductEditor = ({
  product,
  onClose,
  initialCategory,
  suggestedAttributes = [],
  targetCompanyId = null,
}) => {
  const { createProduct, updateProduct, createProductForCompany, updateProductForCompany } = useProducts();
  const { uploadImage } = useItems();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  // State for the form
  const [formData, setFormData] = useState({
    name: { nl: "" },
    description: { nl: "" },
    price: "",
    stock: 0,
    sku: "",
    category: "",
    delivery_time: "",
    discount_percentage: "",
    images: [],
    attributes: [],
    // New structure for variants: { options: [], items: [] }
    variants: { options: [], items: [] },
  });

  // State for adding new options
  const [newOptionName, setNewOptionName] = useState("");
  const [newOptionValue, setNewOptionValue] = useState("");
  const [activeOptionIndex, setActiveOptionIndex] = useState(null);

  useEffect(() => {
    if (product) {
      // Handle legacy variants (array) vs new structure (object)
      let initialVariants = { options: [], items: [] };
      if (
        product.variants &&
        !Array.isArray(product.variants) &&
        product.variants.options
      ) {
        initialVariants = product.variants;
      } else if (
        Array.isArray(product.variants) &&
        product.variants.length > 0
      ) {
        // Try to migrate legacy flat variants if possible, or just keep them as items with no options
        // For now, we'll just put them in items and leave options empty
        initialVariants = { options: [], items: product.variants };
      }

      setFormData({
        name: product.name || { nl: "" },
        description: product.description || { nl: "" },
        price: product.price || "",
        stock: product.stock || 0,
        sku: product.sku || "",
        category: product.category || "",
        delivery_time: product.delivery_time || "",
        discount_percentage:
          product.discount_percentage == null ? "" : product.discount_percentage,
        images: product.images || [],
        attributes: Array.isArray(product.attributes) ? product.attributes : [],
        variants: initialVariants,
      });
    } else if (initialCategory) {
      setFormData((prev) => ({ ...prev, category: initialCategory }));
    }
  }, [product, initialCategory]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNameChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      name: { ...prev.name, nl: value },
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: { ...prev.description, nl: value },
    }));
  };

  // --- Option Management ---

  const addOptionType = () => {
    if (!newOptionName.trim()) return;
    setFormData((prev) => ({
      ...prev,
      variants: {
        ...prev.variants,
        options: [
          ...prev.variants.options,
          { name: newOptionName.trim(), values: [] },
        ],
      },
    }));
    setNewOptionName("");
  };

  const removeOptionType = (index) => {
    setFormData((prev) => {
      const newOptions = prev.variants.options.filter((_, i) => i !== index);
      return {
        ...prev,
        variants: { ...prev.variants, options: newOptions },
      };
    });
  };

  const addOptionValue = (optionIndex) => {
    if (!newOptionValue.trim()) return;
    setFormData((prev) => {
      const newOptions = [...prev.variants.options];
      const currentValues = newOptions[optionIndex].values;
      if (!currentValues.includes(newOptionValue.trim())) {
        newOptions[optionIndex] = {
          ...newOptions[optionIndex],
          values: [...currentValues, newOptionValue.trim()],
        };
      }
      return {
        ...prev,
        variants: { ...prev.variants, options: newOptions },
      };
    });
    setNewOptionValue("");
  };

  const removeOptionValue = (optionIndex, valueToRemove) => {
    setFormData((prev) => {
      const newOptions = [...prev.variants.options];
      newOptions[optionIndex] = {
        ...newOptions[optionIndex],
        values: newOptions[optionIndex].values.filter(
          (v) => v !== valueToRemove,
        ),
      };
      return {
        ...prev,
        variants: { ...prev.variants, options: newOptions },
      };
    });
  };

  // --- Variant Generation ---

  const generateVariants = () => {
    const options = formData.variants.options;
    if (options.length === 0) return;

    // Helper to generate cartesian product
    const cartesian = (a) =>
      a.reduce((a, b) => a.flatMap((d) => b.map((e) => [d, e].flat())), [[]]);

    const values = options.map((o) => o.values);
    // If any option has no values, we can't generate combinations
    if (values.some((v) => v.length === 0)) {
      alert("Zorg dat elke optie minstens één waarde heeft.");
      return;
    }

    const combinations = cartesian(values);

    const newItems = combinations.map((combo) => {
      // combo is array of values corresponding to options
      // e.g. ["S", "Rood"]
      const attributes = {};
      options.forEach((opt, index) => {
        attributes[opt.name] = Array.isArray(combo) ? combo[index] : combo; // Handle single option case
      });

      // Check if this combination already exists to preserve data
      const existing = formData.variants.items.find(
        (item) =>
          JSON.stringify(item.attributes) === JSON.stringify(attributes),
      );

      if (existing) return existing;

      return {
        attributes,
        price: formData.price || "",
        stock: formData.stock || 0,
        sku: formData.sku
          ? `${formData.sku}-${Object.values(attributes).join("-")}`
          : "",
        active: true,
      };
    });

    setFormData((prev) => ({
      ...prev,
      variants: {
        ...prev.variants,
        items: newItems,
      },
    }));

    setActiveTab("variants"); // Switch to variants tab
  };

  const updateVariantItem = (index, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.variants.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return {
        ...prev,
        variants: { ...prev.variants, items: newItems },
      };
    });
  };
  const handleVariantImageUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      const compressedDataUrl = await smartCompressImage(file);
      const resp = await uploadImage(compressedDataUrl);
      const imageKey = resp?.data?.imageKey || resp?.data?.imageId;
      if (imageKey) {
        updateVariantItem(index, "image", imageKey);
      }
    } catch (error) {
      console.error("Failed to upload variant image", error);
      alert("Afbeelding uploaden mislukt");
    } finally {
      setLoading(false);
    }
  };
  // --- Image Upload ---

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setLoading(true);
    try {
      const newImageKeys = [];
      for (const file of files) {
        const compressedDataUrl = await smartCompressImage(file);
        const resp = await uploadImage(compressedDataUrl);
        const imageKey = resp?.data?.imageKey || resp?.data?.imageId;
        if (imageKey) {
          newImageKeys.push(imageKey);
        }
      }
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...newImageKeys],
      }));
    } catch (error) {
      console.error("Failed to upload images", error);
      alert("Afbeeldingen uploaden mislukt");
    } finally {
      setLoading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, images: newImages }));
  };

  const getImageUrl = (imageKey) => {
    if (!imageKey) return null;
    if (imageKey.startsWith("http")) return imageKey;
    return `${S3_BASE_URL}/${imageKey}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (product) {
        if (targetCompanyId) {
          await updateProductForCompany(targetCompanyId, product.id, formData);
        } else {
          await updateProduct(product.id, formData);
        }
      } else {
        if (targetCompanyId) {
          await createProductForCompany(targetCompanyId, formData);
        } else {
          await createProduct(formData);
        }
      }
      onClose();
    } catch (error) {
      console.error("Failed to save product", error);
      alert("Product opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  const tabItems = [
    { key: "general", label: "Algemeen" },
    { key: "options", label: "Opties & Kenmerken" },
    {
      key: "variants",
      label: `Varianten${formData.variants.items.length > 0 ? ` (${formData.variants.items.length})` : ""}`,
    },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6 h-full flex flex-col">
      {/* Custom Tabs */}
      <div
        className={`flex gap-1 p-1 rounded-lg ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
      >
        {tabItems.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 h-8 rounded-md text-[12px] font-medium transition-colors ${
              activeTab === tab.key
                ? isDark
                  ? "bg-white/[0.08] text-white"
                  : "bg-white text-gray-900 shadow-sm"
                : isDark
                  ? "text-gray-500 hover:text-gray-300"
                  : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* General Tab */}
        {activeTab === "general" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Naam (NL)
                </label>
                <Input
                  value={formData.name.nl || ""}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                />
              </div>
              <div className="space-y-1.5">
                <label
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Categorie
                </label>
                <Input
                  value={formData.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                  placeholder="bv. Kleding, Elektronica"
                  className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Beschrijving (NL)
              </label>
              <Textarea
                value={formData.description.nl || ""}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                className={`min-h-[100px] text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Standaard Prijs (€)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  required
                  className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                />
              </div>
              <div className="space-y-1.5">
                <label
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Standaard Voorraad
                </label>
                <Input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => handleChange("stock", e.target.value)}
                  className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                />
              </div>
              <div className="space-y-1.5">
                <label
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Standaard SKU
                </label>
                <Input
                  value={formData.sku}
                  onChange={(e) => handleChange("sku", e.target.value)}
                  className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Levertermijn
                </label>
                <Input
                  value={formData.delivery_time}
                  onChange={(e) => handleChange("delivery_time", e.target.value)}
                  placeholder="bv. 3-5 werkdagen"
                  className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                />
                <p
                  className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  Vrije tekst, zichtbaar op de productpagina. Leeg = niets tonen.
                </p>
              </div>
              <div className="space-y-1.5">
                <label
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Korting (%)
                </label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.discount_percentage}
                  onChange={(e) =>
                    handleChange("discount_percentage", e.target.value)
                  }
                  placeholder="bv. 10"
                  className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                />
                <p
                  className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  Leeg of 0 = geen korting. Geldt ook voor alle varianten.
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Afbeeldingen
              </label>
              <div className="grid grid-cols-4 gap-3">
                {formData.images.map((imgKey, index) => (
                  <div
                    key={index}
                    className={`relative aspect-square rounded-lg overflow-hidden group ${isDark ? "bg-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
                  >
                    <img
                      src={getImageUrl(imgKey)}
                      alt={`Product ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          "https://via.placeholder.com/150?text=Error";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label
                  className={`rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors aspect-square border-2 border-dashed ${isDark ? "border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.02]" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}
                >
                  <ImageIcon
                    className={`h-6 w-6 mb-1.5 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                  />
                  <span
                    className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Upload
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={loading}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Options Tab */}
        {activeTab === "options" && (
          <div className="space-y-5">
            <div
              className={`p-3 rounded-lg ${isDark ? "bg-blue-500/8 border border-blue-500/15" : "bg-blue-50 border border-blue-100"}`}
            >
              <h3
                className={`text-[13px] font-medium flex items-center gap-2 ${isDark ? "text-blue-300" : "text-blue-900"}`}
              >
                <Settings2 className="h-3.5 w-3.5" />
                Product Opties Configureren
              </h3>
              <p
                className={`text-[12px] mt-1 ${isDark ? "text-blue-400/70" : "text-blue-700"}`}
              >
                Voeg hier opties toe zoals "Maat" of "Kleur". Daarna kun je
                waarden toevoegen (bv. S, M, L).
              </p>
            </div>

            <div className="space-y-4">
              {formData.variants.options.map((option, index) => (
                <div
                  key={index}
                  className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <h4
                      className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {option.name}
                    </h4>
                    <button
                      type="button"
                      className={`text-[12px] text-red-500 hover:text-red-400 flex items-center gap-1`}
                      onClick={() => removeOptionType(index)}
                    >
                      <Trash2 className="h-3 w-3" /> Verwijder
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {option.values.map((val, vIndex) => (
                      <span
                        key={vIndex}
                        className={`text-[12px] px-2 py-1 rounded-md flex items-center gap-1.5 ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-100 text-gray-700"}`}
                      >
                        {val}
                        <button
                          type="button"
                          onClick={() => removeOptionValue(index, val)}
                          className={`${isDark ? "text-gray-500 hover:text-red-400" : "text-gray-400 hover:text-red-500"}`}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>

                  <div className="flex gap-2 max-w-sm">
                    <Input
                      placeholder={`Nieuwe waarde voor ${option.name}...`}
                      value={activeOptionIndex === index ? newOptionValue : ""}
                      onChange={(e) => {
                        setActiveOptionIndex(index);
                        setNewOptionValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addOptionValue(index);
                        }
                      }}
                      className={`h-8 text-[12px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                    />
                    <button
                      type="button"
                      onClick={() => addOptionValue(index)}
                      className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}

              <div
                className={`flex gap-2 items-end pt-4 mt-2 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
              >
                <div className="space-y-1.5 flex-1 max-w-xs">
                  <label
                    className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Nieuwe Optie Naam
                  </label>
                  <Input
                    placeholder="bv. Materiaal"
                    value={newOptionName}
                    onChange={(e) => setNewOptionName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addOptionType();
                      }
                    }}
                    className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={addOptionType}
                  className={`h-9 px-3 rounded-lg text-[12px] font-medium flex items-center gap-1.5 border transition-colors mb-0.5 ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                >
                  <Plus className="h-3.5 w-3.5" /> Optie Toevoegen
                </button>
              </div>
            </div>

            {formData.variants.options.length > 0 && (
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={generateVariants}
                  className={`h-8 px-3 rounded-lg text-[12px] font-medium flex items-center gap-1.5 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Genereer Varianten
                </button>
              </div>
            )}
          </div>
        )}

        {/* Variants Tab */}
        {activeTab === "variants" && (
          <div className="space-y-4">
            {formData.variants.items.length === 0 ? (
              <div
                className={`text-center py-12 border-2 border-dashed rounded-xl ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`}
              >
                <Settings2
                  className={`h-10 w-10 mx-auto mb-3 ${isDark ? "text-gray-700" : "text-gray-300"}`}
                />
                <h3
                  className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Nog geen varianten
                </h3>
                <p
                  className={`text-[12px] mt-1 mb-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Ga naar "Opties" om eigenschappen toe te voegen en varianten
                  te genereren.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab("options")}
                  className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                >
                  Naar Opties
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span
                    className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {formData.variants.items.length} Varianten
                  </span>
                  <button
                    type="button"
                    onClick={generateVariants}
                    className={`h-7 px-2.5 rounded-lg text-[11px] font-medium flex items-center gap-1 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                  >
                    <RefreshCw className="h-3 w-3" /> Her-genereer
                  </button>
                </div>

                <div
                  className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
                >
                  <Table>
                    <TableHeader>
                      <TableRow
                        className={
                          isDark ? "border-white/[0.06]" : "border-gray-100"
                        }
                      >
                        <TableHead
                          className={`w-[50px] text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                        >
                          Img
                        </TableHead>
                        <TableHead
                          className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                        >
                          Variant
                        </TableHead>
                        <TableHead
                          className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                        >
                          Prijs (€)
                        </TableHead>
                        <TableHead
                          className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                        >
                          Voorraad
                        </TableHead>
                        <TableHead
                          className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                        >
                          SKU
                        </TableHead>
                        <TableHead
                          className={`w-[40px] ${isDark ? "bg-white/[0.02]" : "bg-gray-50/50"}`}
                        ></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.variants.items.map((item, index) => (
                        <TableRow
                          key={index}
                          className={
                            isDark ? "border-white/[0.04]" : "border-gray-50"
                          }
                        >
                          <TableCell>
                            <label
                              className={`cursor-pointer block w-9 h-9 rounded-lg border border-dashed overflow-hidden ${isDark ? "border-white/[0.08] hover:border-white/[0.15]" : "border-gray-200 hover:border-gray-300"}`}
                            >
                              {item.image ? (
                                <img
                                  src={getImageUrl(item.image)}
                                  alt="Variant"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div
                                  className={`w-full h-full flex items-center justify-center ${isDark ? "text-gray-600 bg-white/[0.02]" : "text-gray-300 bg-gray-50"}`}
                                >
                                  <ImageIcon className="h-3.5 w-3.5" />
                                </div>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) =>
                                  handleVariantImageUpload(index, e)
                                }
                              />
                            </label>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(item.attributes).map(
                                ([key, val]) => (
                                  <span
                                    key={key}
                                    className={`text-[11px] px-1.5 py-0.5 rounded-md ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-100 text-gray-600"}`}
                                  >
                                    <span
                                      className={
                                        isDark
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }
                                    >
                                      {key}:
                                    </span>{" "}
                                    {val}
                                  </span>
                                ),
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder={formData.price || "0.00"}
                              value={item.price}
                              onChange={(e) =>
                                updateVariantItem(
                                  index,
                                  "price",
                                  e.target.value,
                                )
                              }
                              className={`w-20 h-7 text-[12px] rounded-md ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              placeholder={formData.stock || "0"}
                              value={item.stock}
                              onChange={(e) =>
                                updateVariantItem(
                                  index,
                                  "stock",
                                  e.target.value,
                                )
                              }
                              className={`w-20 h-7 text-[12px] rounded-md ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              placeholder={`${formData.sku || "SKU"}-${index + 1}`}
                              value={item.sku}
                              onChange={(e) =>
                                updateVariantItem(index, "sku", e.target.value)
                              }
                              className={`w-28 h-7 text-[12px] rounded-md ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                            />
                          </TableCell>
                          <TableCell>
                            <button
                              type="button"
                              className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors text-red-500 ${isDark ? "hover:bg-red-500/10" : "hover:bg-red-50"}`}
                              onClick={() => {
                                const newItems = formData.variants.items.filter(
                                  (_, i) => i !== index,
                                );
                                setFormData((prev) => ({
                                  ...prev,
                                  variants: {
                                    ...prev.variants,
                                    items: newItems,
                                  },
                                }));
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className={`flex justify-end gap-2 pt-4 border-t mt-auto ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
      >
        <button
          type="button"
          onClick={onClose}
          className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
        >
          Annuleren
        </button>
        <button
          type="submit"
          disabled={loading}
          className={`h-8 px-4 rounded-lg text-[12px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200 disabled:opacity-50" : "bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"}`}
        >
          {loading ? "Opslaan..." : "Product Opslaan"}
        </button>
      </div>
    </form>
  );
};

export default ProductEditor;
